/**
 * PCI & Hidden Fees Checker capture form — /hidden-fees-checker, shown under
 * the result. Replaces the old inline LeadForm and the "email me the results"
 * pill.
 *
 * The checker passes its inputs and result as `context` (see
 * HiddenFeesChecker.jsx → buildLeadContext). Keys read here:
 *   score, category, flags_raised, monthly_worth_reviewing_gbp,
 *   pci_fee_gbp_per_month, terminal_rental_gbp_per_month (total),
 *   terminal_count, auth_fee_gbp, service_fee_gbp_per_month,
 *   other_fees_gbp_per_month.
 * Everything in context also lands in the Lead's Note.
 */
import { company, currentProvider, email, fullName, phone } from "./common";

const has = (v) => typeof v === "number" && Number.isFinite(v);

function terminalText(ctx) {
  if (!has(ctx.terminal_rental_gbp_per_month)) return null;
  const count = has(ctx.terminal_count)
    ? ` (${ctx.terminal_count} terminal${ctx.terminal_count === 1 ? "" : "s"})`
    : "";
  return `Terminal rental £${ctx.terminal_rental_gbp_per_month}/mo total${count}`;
}

/** "PCI £10/mo · Terminal rental £25/mo total (1 terminal) · Auth £0.05/txn · …" — only what was entered. */
export function hiddenFeesRatesText(ctx) {
  return [
    has(ctx.pci_fee_gbp_per_month) ? `PCI £${ctx.pci_fee_gbp_per_month}/mo` : null,
    terminalText(ctx),
    has(ctx.auth_fee_gbp) ? `Auth £${ctx.auth_fee_gbp}/txn` : null,
    has(ctx.service_fee_gbp_per_month) ? `Service fee £${ctx.service_fee_gbp_per_month}/mo` : null,
    has(ctx.other_fees_gbp_per_month) ? `Other fees £${ctx.other_fees_gbp_per_month}/mo` : null,
  ].filter(Boolean).join(" · ");
}

const toolHiddenFees = {
  id: "tool_hidden_fees",
  label: "Hidden Fees Checker",
  crm: { layout: "Standard", leadSource: "Website Calculator - Hidden Fees" },
  fields: [
    [fullName, company],
    [email, { ...phone, help: "We'll only call to discuss your results — never spam." }],
    currentProvider,
  ],
  submitLabel: "Get my free review",
  consent: { variant: "standard" },
  success: {
    title: "Thank you",
    body: "We've got your fee check and details. We'll be in touch with your personalised review shortly.",
  },
  contextToCrm: (ctx) => ({
    Current_rates: hiddenFeesRatesText(ctx),
    Number_of_card_machines: ctx.terminal_count,
  }),
  summary: (values, ctx) => {
    if (!has(ctx.score)) return "Hidden Fees Checker";
    const parts = [`Hidden Fees Checker: ${ctx.score}/100`];
    if (ctx.category) parts[0] += ` (${ctx.category.toLowerCase()})`;
    if (has(ctx.flags_raised)) parts.push(`${ctx.flags_raised} flag${ctx.flags_raised === 1 ? "" : "s"}`);
    if (has(ctx.monthly_worth_reviewing_gbp)) parts.push(`£${ctx.monthly_worth_reviewing_gbp.toFixed(2)}/mo worth reviewing`);
    return parts.join(", ");
  },
};

export default toolHiddenFees;
